import React, { MouseEvent, useCallback, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import type { MaskTemplateTypes } from '../../../core';
import { colors, utils } from '../../../core';
import Icon from '../Icon';
import Text, { TextProps } from '../Text';

export interface TextMaskProps {
  visible: boolean;
  children: string;
  as?: MaskTemplateTypes;
}

const TextWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 40px 4px 12px;
  border-radius: 16px;
  position: relative;

  & svg {
    display: none;
    transition: display 0.4s ease;
  }

  &:hover {
    background-color: ${colors.pearl};
    cursor: pointer;
    transition: background-color 0.4s ease;

    & svg {
      display: block;
    }
  }
`;

const IconContainer = styled.div`
  position: absolute;
  right: 12px;
  top: 0;
  padding: 8px 0;
`;

const CustomText = styled(Text)`
  padding: 0;
  margin: 0;
  font-weight: 400;
`;

const CustomIcon = styled(Icon)`
  cursor: pointer;
  margin-left: 12px;
`;

const TextMask = ({
  visible,
  children,
  as = 'default',
  ...textProps
}: TextMaskProps & TextProps) => {
  const [isVisible, setIsVisible] = useState(visible);
  const theme = useTheme();

  const toggleVisibility = (e: MouseEvent) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  const renderText = useCallback(() => {
    if (isVisible) {
      return children;
    } else {
      return utils.maskText(children, as);
    }
  }, [isVisible]);

  return (
    <TextWrapper onClick={toggleVisibility}>
      <CustomText {...textProps} noMargin>
        {renderText()}
      </CustomText>

      <IconContainer>
        <CustomIcon type="view" color={textProps.color || theme.fontColor || colors.ash} />
      </IconContainer>
    </TextWrapper>
  );
};

export default TextMask;
